// Small helpers that compute with date values only (`"YYYY-MM-DD"`, the same form
// as `<input type="date">`). The calendar (calendar.tsx) uses them.
//
// Promise:
// - A value never goes through `new Date("YYYY-MM-DD")`: that reads it as UTC
//   midnight, which is the previous day west of UTC. Values become a Date only
//   from their year, month and day on the local clock.
// - "Today" is the wall-clock day in the given time zone (the account's), or the
//   browser's when unset.
import { zonedParts } from "../time/format";

export interface YearMonth {
  year: number;
  /** 1 to 12. */
  month: number;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Splits `"YYYY-MM-DD"` into year, month and day. Null for another form or a day that does not exist. */
export function parseDate(
  value: string,
): { year: number; month: number; day: number } | null {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!r) return null;
  const year = Number(r[1]);
  const month = Number(r[2]);
  const day = Number(r[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month))
    return null;
  return { year, month, day };
}

export function formatDateValue(
  year: number,
  month: number,
  day: number,
): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Today in `timeZone` (IANA; undefined = the browser's). */
export function todayValue(now = new Date(), timeZone?: string): string {
  const p = zonedParts(now, timeZone);
  return formatDateValue(p.year, p.month, p.day);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** The value as a Date at local midnight (for formatting only). */
export function toLocalDate(value: string): Date | null {
  const p = parseDate(value);
  return p ? new Date(p.year, p.month - 1, p.day) : null;
}

/** Adds days (subtracts when negative), across months and years. */
export function addDays(value: string, n: number): string {
  const d = toLocalDate(value);
  if (!d) return value;
  d.setDate(d.getDate() + n);
  return formatDateValue(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Adds months. The day is cut to the end of the target month (Jan 31 + 1 month = Feb 28). */
export function addMonths(value: string, n: number): string {
  const p = parseDate(value);
  if (!p) return value;
  const ym = shiftMonth({ year: p.year, month: p.month }, n);
  return formatDateValue(
    ym.year,
    ym.month,
    Math.min(p.day, daysInMonth(ym.year, ym.month)),
  );
}

export function shiftMonth(ym: YearMonth, n: number): YearMonth {
  const i = ym.year * 12 + (ym.month - 1) + n;
  return { year: Math.floor(i / 12), month: (i % 12) + 1 };
}

export function monthOf(value: string): YearMonth | null {
  const p = parseDate(value);
  return p ? { year: p.year, month: p.month } : null;
}

/**
 * The grid of a month: 6 weeks x 7 days = 42 days starting on Sunday, filled
 * with days of the months before and after (a row count that changes per month
 * would make the panel's height jump).
 */
export function monthGrid(ym: YearMonth): string[] {
  const first = formatDateValue(ym.year, ym.month, 1);
  const lead = new Date(ym.year, ym.month - 1, 1).getDay();
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/**
 * Whether `value` lies within min / max (either may be empty = no limit).
 * Values of a fixed-width form (`"YYYY-MM-DD"`, `"HH:MM"`) compare as strings.
 */
export function inRange(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return false;
  if (max && value > max) return false;
  return true;
}

/** Moves `value` into min / max. */
export function clampDate(value: string, min?: string, max?: string): string {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}
